import { useContext, useMemo } from 'react';
import { CopyRegular } from '@fluentui/react-icons';
import { TOverflowButtonParam } from '@shared/components';
import { ToastContext } from '@shared/contexts';
import { buildHref } from '@shared/utils';
import { getItemPathByItemType } from '@modules/documents';
import getReferenceItemLabel from '@modules/documents/helpers/getReferenceItemLabel.helper';
import { TItem } from '../types/items.types';

type TProps = {
  item: Pick<TItem, '_id' | 'name' | 'type'>;
};

const useExternalLinkNavAction = ({ item }: TProps) => {
  const { show } = useContext(ToastContext);
  const { _id, name, type } = item;

  const externalLinkNavActionButton = useMemo<TOverflowButtonParam>(() => {
    // Rich text editors paste a link named after the item, plain text fields paste the url.
    const copyExternalLink = async () => {
      const url = buildHref(`${window.location.origin}/${getItemPathByItemType(type)}/${_id}`, { view: 'approved' });
      const link = document.createElement('a');
      link.href = url;
      link.textContent = getReferenceItemLabel(name);

      try {
        if (typeof ClipboardItem === 'undefined') await navigator.clipboard.writeText(url);
        else
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([link.outerHTML], { type: 'text/html' }),
              'text/plain': new Blob([url], { type: 'text/plain' }),
            }),
          ]);
        show('success', 'Copied to clipboard.');
      } catch (error) {
        console.error('Copy failed', error);
        show('error', 'Copying the link failed!');
      }
    };

    return {
      children: 'External link',
      icon: <CopyRegular />,
      value: 'externalLink',
      onClick: copyExternalLink,
    };
  }, [_id, name, type]);

  return { externalLinkNavActionButton };
};

export default useExternalLinkNavAction;
